import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysUser } from '../queryKeys';

import { fetchUserAdmin } from './fetchUserAdmin';
import { type UserAdmin } from './types';

/**
 * Generates the query options for the admin user query.
 *
 * @param userId - The ID of the user to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getUserAdminQueryOptions = <TData>({
  userId,
  options,
}: {
  userId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<UserAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => ({
  enabled: Boolean(userId),
  queryFn: async () => fetchUserAdmin(userId!),
  queryKey: queryKeysUser.getAdmin(userId ?? ''),
  staleTime: ms('60s'), // prevent hitting the rate limit
  ...options,
});

/**
 * React Query based custom hook for getting the data for a user with a given userId as admin.
 * Requires ADMIN access.
 *
 * @param userId - The ID of the user to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_user_by_id_vadmin_user__user_id__get
 */
export const useQueryUserAdmin = (
  userId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<UserAdmin, Error, UserAdmin>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<UserAdmin, Error, UserAdmin>(
    getUserAdminQueryOptions<UserAdmin>({ userId, options }),
  );
